import { useEffect, useState, useContext, useRef } from 'react'
import ZoomVideo, { LocalVideoTrack } from '@zoom/videosdk'
import { ConferenceContext } from 'contexts/conference'
import { styled } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Action, Icon } from 'hello-design-system'
import { AlertContent, Tile } from 'pages/Diagnostics/styles'
import { fetchButtonData } from 'pages/teacher/classroom/components/breakout-rooms/breakout.service'
import { VideoBackgroundDialogComponent } from '../dialog'
import { ButtonTexts } from 'utils/constants'
import { base64ToBlob, fetchImageAsBlobURL } from './VirtualBackgroundHelpers'

const StyledBox = styled(Box)({
    display: 'flex',
    fontWeight: 'bold',
    width: '100%'
})

const VideoContainer = styled(Box)(({ theme }) => ({
    width: '400px',
    marginRight: theme.spacing(2)
}))

const VideoTile = styled(Box)(({ theme }) => ({
    width: '100%',
    height: theme.spacing(27.5),
    marginBottom: theme.spacing(1)
}))

const BackgroundContainer = styled(Box)(({ theme }) => ({
    width: theme.spacing(57.5),
    maxHeight: theme.spacing(40.75),
    overflow: 'auto'
}))

const BackgroundList = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(3, ${theme.spacing(17)})`,
    gap: theme.spacing(2)
}))

const BackgroundHead = styled(Box)(({ theme }) => ({
    fontSize: theme.spacing(2),
    fontWeight: 600,
    marginBottom: theme.spacing(2)
}))

const VBGrid = styled(Box)<{ active: boolean }>(({ theme, active }) => ({
    width: theme.spacing(17.75),
    height: theme.spacing(10),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    cursor: 'pointer',
    border: active ? `${theme.spacing(0.125)} solid ${theme.palette.primary?.main}` : 'none'
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    alignItems: 'center',
    fontSize: theme.spacing(1.75),
    fontWeight: 600,
    color: theme.palette.grey?.[700]
}))

const VBImg = styled('img')(({ theme }) => ({
    borderRadius: theme.spacing(1),
    width: '100%',
    height: '100%'
}))

const { Cancel, ChooseBackground, ResetBackground } = ButtonTexts

interface VirtualBackgroundListInterface {
    imageName: string
    imageType: number
    imageUrl: string
}

const VBDialog: React.FC<{
    close: () => void
    setBackgroundSelected: React.Dispatch<React.SetStateAction<{ active: boolean; url: string; change: string }>>
    selectedBG: string
    selectedCameraId: string
    wasVideoInitiallyOn?: Boolean
}> = ({ close, setBackgroundSelected, selectedBG, selectedCameraId, wasVideoInitiallyOn }) => {
    const [backgroundLists, setBackgroundLists] = useState<string[]>([])
    const conference = useContext(ConferenceContext)
    const localVideoTrackRef = useRef<LocalVideoTrack | null>(null)

    const stopTrack = async (): Promise<void> => {
        if (localVideoTrackRef.current) {
            try {
                await localVideoTrackRef.current.updateVirtualBackground('')
                await localVideoTrackRef.current.stop()
            } catch (error) {
                console.log('VirtualBackground DEBUG: Ignored error while stopping track:', error)
            } finally {
                localVideoTrackRef.current = null
            }
        }
    }

    const handleVbPopupreset = (): void => {
        setBackgroundSelected(prev => ({ ...prev, change: '' }))
        void handleBgChange('')
    }

    const handleVbPopupClose = async (): Promise<void> => {
        setBackgroundSelected(prev => ({ ...prev, active: false, change: prev.url }))

        if (!wasVideoInitiallyOn && conference.status === 'session-joined') {
            void conference.rtcSession.setVideoMuted(false)
        }

        // Stop and release local video track securely before releasing the popup
        await stopTrack()
        close()
    }

    const handleVbPopupSelect = async (): Promise<void> => {
        // Update state
        setBackgroundSelected(prev => ({ ...prev, active: false, url: selectedBG }))

        // Retrieve and parse background list safely
        const backgroundList = sessionStorage.getItem('studentVirtualBackgroundList')
        const virtualBackgroundList = (JSON.parse(backgroundList || '[]')[0]?.virtualBackgroundList || []) as VirtualBackgroundListInterface[]

        // Get selected background ID1
        const selectedBGId = virtualBackgroundList.find(list => list.imageUrl === selectedBG)?.imageName || ''

        // Update session storage
        if (selectedBG) {
            sessionStorage.setItem('selectedBackgroundId', selectedBGId)
            sessionStorage.setItem('studentSelectedBackground', selectedBG)
        } else {
            sessionStorage.removeItem('selectedBackgroundId')
            sessionStorage.removeItem('studentSelectedBackground')
        }

        // If video is muted, unmute it early
        if (conference.status === 'session-joined') {
            void conference.rtcSession.setVideoMuted(false)
        }

        // AWAIT stopping the dialog video before triggering the parent close
        // This prevents the parent from racing to start a camera that is softly locked
        await stopTrack()
        close()
    }

    const actions: Action[] = [
        fetchButtonData(ResetBackground, 'outlined', handleVbPopupreset),
        fetchButtonData(Cancel, 'outlined', () => { void handleVbPopupClose() }),
        fetchButtonData(ChooseBackground, 'contained', () => { void handleVbPopupSelect() })
    ]

    useEffect(() => {
        setTimeout(() => { void handleBgChange(selectedBG) }, 100)
        const backgroundList = sessionStorage.getItem('studentVirtualBackgroundList')
        const virtualBackgroundList = (JSON.parse(backgroundList || '[]')[0]?.virtualBackgroundList || []) as VirtualBackgroundListInterface[]
        setBackgroundLists(virtualBackgroundList.map(({ imageUrl }) => imageUrl))

        return () => {
            void stopTrack()
        }
    }, [])

    const handleBgChange = async (bg: string): Promise<void> => {
        setBackgroundSelected(prev => ({ ...prev, change: bg }))

        const videoElement = document.getElementById('virtual-background-popup-panel') as HTMLVideoElement
        if (!videoElement) return

        videoElement.style.transform = conference.status === 'session-joined' ? 'scaleX(1)' : 'scaleX(-1)'

        let backgroundImage: string | undefined = bg

        if (!['blur', ''].includes(bg)) {
            try {
                if (bg.startsWith('data:image')) {
                    const blob = base64ToBlob(bg)
                    backgroundImage = URL.createObjectURL(blob)
                } else {
                    backgroundImage = await fetchImageAsBlobURL(bg)
                }
            } catch (error) {
                console.error('Error loading image:', error)
                return
            }
        }

        let track = localVideoTrackRef.current

        // If video track not already created, create and start the track
        if (!track) {
            track = await ZoomVideo.createLocalVideoTrack(selectedCameraId)
            if (!track) throw new Error('Failed to create video track.')

            localVideoTrackRef.current = track

            try {
                if (backgroundImage === 'blur') {
                    await track.start(videoElement)
                    await track.updateVirtualBackground('blur')
                } else if (backgroundImage) {
                    try {
                        await track.start(videoElement, { imageUrl: backgroundImage })
                    } catch (e) {
                        await track.start(videoElement)
                        await track.updateVirtualBackground(backgroundImage)
                    }
                } else {
                    await track.start(videoElement)
                }
            } catch (err) {
                console.error('Error starting video track for virtual background:', err)
            }
        } else {
            // If video track is already started, simply update the background
            try {
                if (backgroundImage) {
                    await track.updateVirtualBackground(backgroundImage)
                } else {
                    // Zoom SDK requires undefined to explicitly clear the virtual background
                    await track.updateVirtualBackground(undefined as any)
                }
            } catch (err) {
                console.error('Error updating virtual background:', err)
            }
        }

        if (backgroundImage?.startsWith('blob:')) {
            setTimeout(() => { if (backgroundImage) URL.revokeObjectURL(backgroundImage) }, 5000)
        }
    }

    return (
        <>
            <VideoBackgroundDialogComponent
                open={true}
                onClose={() => { void handleVbPopupClose() }}
                title='Video Backgrounds'
                actions={actions}
                maxWidth={'md'}
                showCloseIcon={true}
                fullScreen={false}
            >
                <StyledBox>
                    <VideoContainer>
                        <VideoTile>
                            <Tile showVideo={true}>
                                <video-player-container
                                    id='video-player-container-diagnostic'
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        overflow: 'hidden',
                                        display: 'block'
                                    }}
                                >
                                    <video-player
                                        id='virtual-background-popup-panel'
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'block',
                                            width: '100%',
                                            height: '100%',
                                            transform: 'scaleX(-1)',
                                            objectFit: 'cover',
                                            willChange: 'transform'
                                        }}
                                    />
                                </video-player-container>
                            </Tile>
                        </VideoTile>
                        <AlertContent
                            severity={'info'}
                            variant='outlined'
                            className='vb-dialog'
                            description={'Others won\'t see your video while you preview.'}
                        />
                    </VideoContainer>
                    <BackgroundContainer>
                        <BackgroundHead>Choose background</BackgroundHead>
                        <BackgroundList>
                            {['', 'blur', ...backgroundLists].map(bg => (
                                <VBGrid
                                    onClick={() => { void handleBgChange(bg) }}
                                    active={bg === selectedBG}
                                    key={bg}
                                >
                                    {!bg || bg === 'blur'
                                        ? <StyledTypography key={bg}>
                                            <Icon iconName={bg === 'blur' ? 'LensBlurRounded' : 'BlockRounded'} />
                                            {bg || 'None'}
                                        </StyledTypography>
                                        : <VBImg src={bg} alt='Background' />}
                                </VBGrid>
                            ))}
                        </BackgroundList>
                    </BackgroundContainer>
                </StyledBox>
            </VideoBackgroundDialogComponent>
        </>
    )
}
export { VBDialog }
